import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Activity,
  LayoutDashboard,
  FileText,
  AlertTriangle,
  CheckSquare,
  Settings,
  BarChart3,
  Shield,
  Bell,
  LogOut,
  Users,
  ChevronDown,
  Menu,
  X,
  Upload,
  ClipboardList,
} from 'lucide-react';
import { cn } from '../lib/utils';
import OrgSelector from './OrgSelector';

const navigation = [
  { name: 'Dashboard', href: '/reports', icon: LayoutDashboard, roles: ['super_admin', 'sys_admin', 'org_admin', 'dept_admin', 'clinician', 'technician'] },
  { name: 'Upload Report', href: '/reports/upload', icon: Upload, roles: ['super_admin', 'sys_admin', 'org_admin', 'dept_admin', 'clinician', 'technician'] },
  { name: 'Patients', href: '/patients', icon: Users, roles: ['super_admin', 'sys_admin', 'org_admin', 'dept_admin', 'clinician', 'technician'] },
  { name: 'Order Tests', href: '/orders', icon: ClipboardList, roles: ['super_admin', 'sys_admin', 'org_admin', 'dept_admin', 'clinician'] },
  { name: 'Alert Inbox', href: '/alerts', icon: AlertTriangle, roles: ['super_admin', 'sys_admin', 'org_admin', 'dept_admin', 'clinician'] },
  { name: 'Analytics', href: '/analytics', icon: BarChart3, roles: ['super_admin', 'sys_admin', 'org_admin', 'dept_admin'] },
  { name: 'Admin Console', href: '/admin', icon: Settings, roles: ['super_admin', 'sys_admin', 'org_admin'] },
  { name: 'System Management', href: '/superadmin', icon: Shield, roles: ['super_admin'] },
];

export const AppLayout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const userRole = user?.role || 'clinician';
  const filteredNav = navigation.filter((item) => item.roles.includes(userRole));

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getRoleBadge = (role) => {
    const badges = {
      super_admin: 'Super Admin',
      org_admin: 'Org Admin',
      dept_admin: 'Dept Admin',
      clinician: 'Clinician',
      technician: 'Technician',
    };
    return badges[role] || role;
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="app-layout">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={cn(
        "fixed top-0 left-0 z-50 h-full w-64 bg-white border-r border-slate-200 transform transition-transform duration-200 ease-in-out lg:translate-x-0",
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-slate-200">
            <Link to="/dashboard" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <Activity className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-bold text-slate-900 tracking-tight">meragoAnalyze</span>
            </Link>
            <button 
              className="lg:hidden p-1 rounded-md hover:bg-slate-100"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 py-4">
            <nav className="px-3 space-y-1">
              {filteredNav.map((item) => {
                const isActive = location.pathname === item.href || 
                  (item.href !== '/dashboard' && location.pathname.startsWith(item.href));
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={cn(
                      "nav-item",
                      isActive && "active"
                    )}
                    onClick={() => setSidebarOpen(false)}
                    data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span>{item.name}</span>
                  </Link>
                );
              })}
            </nav>
          </ScrollArea>

          {/* User section */}
          <div className="p-4 border-t border-slate-200">
            <div className="flex items-center gap-3">
              <Avatar className="h-10 w-10 border-2 border-slate-200">
                <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                  {getInitials(user?.full_name)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{user?.full_name}</p>
                <p className="text-xs text-muted-foreground">{getRoleBadge(user?.role)}</p>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top header */}
        <header className="sticky top-0 z-30 h-16 bg-white border-b border-slate-200">
          <div className="flex items-center justify-between h-full px-4 lg:px-6">
            <button
              className="lg:hidden p-2 rounded-md hover:bg-slate-100"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-5 h-5" />
            </button>

            <div className="flex-1" />

            <div className="flex items-center gap-3">
              {/* Org Selector for sys_admin */}
              <OrgSelector />
              
              {/* Notifications */}
              <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
                <Bell className="w-5 h-5" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
              </Button>

              {/* User menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-primary/10 text-primary text-sm font-semibold">
                        {getInitials(user?.full_name)}
                      </AvatarFallback>
                    </Avatar>
                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col">
                      <span>{user?.full_name}</span>
                      <span className="text-xs font-normal text-muted-foreground">{user?.email}</span>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="logout-menu-item">
                    <LogOut className="w-4 h-4 mr-2" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
